import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { Navigate, useLocation } from "react-router-dom";
import { clientMe, clientLogout as apiLogout } from "./clientAuth";

// React context for the currently signed-in CLIENT (not coach).
// Loads on mount; exposes `refresh` and `logout` helpers. Never touches the
// coach auth flow.

const ClientAuthContext = createContext({ client: null, loading: true, refresh: () => {}, logout: async () => {} });

export function ClientAuthProvider({ children }) {
  const [state, setState] = useState({ client: null, loading: true });

  const refresh = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    clientMe()
      .then((c) => setState({ client: c, loading: false }))
      .catch(() => setState({ client: null, loading: false }));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await apiLogout(); } catch (_e) { /* best-effort */ }
    setState({ client: null, loading: false });
  }, []);

  return (
    <ClientAuthContext.Provider value={{ client: state.client, loading: state.loading, refresh, logout }}>
      {children}
    </ClientAuthContext.Provider>
  );
}

export function useClientAuth() {
  return useContext(ClientAuthContext);
}

// Route guard: if not signed in, bounce to /audit/signup preserving the
// intended destination in state.from so the signup screen can send her back.
export function RequireClient({ children }) {
  const { client, loading } = useClientAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-brand-base flex items-center justify-center">
        <Loader2 className="text-brand-primary animate-spin" size={32} strokeWidth={1.5} />
      </div>
    );
  }
  if (!client) {
    return <Navigate to="/audit/signup" replace state={{ from: location.pathname }} />;
  }
  return children;
}
